
import { ISceneAddon, SceneAddonType } from "./ISceneAddon";
import { SignalConfig } from "../../config/SignalConfig";
import { SceneNodeState } from "../ISceneNode";
import { AddonManager } from "./AddonManager";
import { Object3D, DoubleSide, Color, Mesh, Box3, MeshStandardMaterial, Texture } from "three";
import { Main } from "../../Main";
import { NodePlacer } from "../../controller/NodePlacer";
import { Scene } from "../Scene";
import { Building } from "../../building/Building";

interface IRoofAddonConfig
{
	label: string;
	id: string;
	size: {
		length: number;
	};
	material? : THREE.Material;
	mesh: {
		"1": string;
		"2": string;
		"3": string;
		"4": string;
		"5": string;
	};
	pitch: SignalConfig<number>;
	type?: string;
	color?: string;
}

/**
 * Common class to deal with roof addons.
 * ridge vents
 * These can be added on the roof of the building.
 */
export class RoofAddon implements ISceneAddon
{
	public static count = 0;

	public id:string;
	public itemId:string;
	public name:string;
	public type = SceneAddonType.ROOF;
	public parentId:string;

	public hasValidPlace:boolean;
	public state:SceneNodeState;
	public added = false;

	private _itemConfig: IRoofAddonConfig;

	private _length:number;
	private _pos: number;
	private _savedPos:number;
	private _pitch:SignalConfig<number>;
	private _visible = false;

	private _container:THREE.Object3D;
	private _mesh:THREE.Mesh; // one of 4 meshes according to pitch
	private _meshes:{[id:string]:THREE.Mesh};

	private _ambient: number;
	private _material:THREE.Material;

	constructor(itemConfig:IRoofAddonConfig)
	{
		this._itemConfig = itemConfig;

		this.name = itemConfig.label;
		this.itemId = itemConfig.id;
		this.id = itemConfig.id + "_" + (++RoofAddon.count);

		this._length = itemConfig.size.length;
		this._pos = 0;
		this._pitch = itemConfig.pitch;

		// this causes an error when opening certain buildings
		//this.createMeshes(itemConfig);

		// so instead we wait for the addons to load
		this._container = new Object3D();
		this.init();
	}

	private init()
	{
		this.createMeshes(this._itemConfig);

		if (this._visible)
		{
			this.show();
		}
		else
		{
			this.hide();
		}

		if (this._ambient !== undefined)
		{
			this.onColorChange(this._ambient);
		}
	};

	private createMeshes(itemConfig:IRoofAddonConfig)
	{
		this._material = itemConfig.material || this.createMaterial(itemConfig);

		this._meshes = {};

		for (var i = 1; i <= 5; i++)
		{
			var pitch:string = i.toString();

			var meshName = itemConfig.mesh[pitch];
			var mesh = AddonManager.getInstance().createMesh(meshName, this._material);
			//mesh.scale.set(1, 1, 1);

			mesh.visible = false;
			mesh.castShadow = true;
			this._container.add(mesh);
			this._meshes[pitch] = mesh as Mesh;
		}

		// don't show initially (only after position is found)
		this.updatePitch(false);
		this._pitch.signal.add(this.updatePitch);
	}

	public updatePitch = (show:boolean = true, pitch:number = null) =>
	{
		var self = this;

		// show only the mesh for the current pitch

		pitch = pitch || this._pitch.value;

		if (pitch < 0)
		{
			var tan = -pitch / 12;
			var alpha = Math.atan(tan);

			// adjust rotation so eave trim aligns
			// this is quite a hack..
			// setTimeout is needed because updatePitch is called when pitch is changed too

			/*setTimeout(function() {
				self._container.rotation.z = alpha * 2;
			}, 0);*/

			requestAnimationFrame(() =>
			{
				self._container.rotation.z = alpha * 2;
			});

			pitch = 1;
		}
		else
		{
			this._container.rotation.z = 0;
		}

		for (var key in this._meshes)
		{
			var visible = (parseFloat(key) == pitch);
			this._meshes[key].visible = show && visible && this._visible;
			if (visible)
			{
				this._mesh = this._meshes[key];
			}
		}
	};

	private createMaterial(itemConfig:any)
	{
		var color = itemConfig.color;

		if (color === "vent")
		{
			var signalConfig = Main.getInstance().config.colors.vent;
			color = signalConfig.value;
		}

		if (signalConfig)
		{
			signalConfig.signal.add(this.onColorChange);
		}

		var material = new MeshStandardMaterial({
			color: color,
			side: DoubleSide,
			envMap: Main.getInstance().content.scene3D.background as Texture,
			roughness: 1,
			metalness: 0,
		});

		return material;
	}

	private onColorChange = (value:number) =>
	{
		this._ambient = value;

		if (this._material)
		{
			(<THREE.MeshStandardMaterial>this._material).color = new Color(value);
		}
	};

	public getBoundingBox():THREE.Mesh
	{
		return null;
	}

	public getBBox(): Box3
	{
		return new Box3().setFromObject(this._container);
	}

	public addToScene(scene:THREE.Object3D)
	{
		scene.add(this._container);
	}

	public removeFromScene(scene:THREE.Object3D)
	{
		scene.remove(this._container);

		// seems like a three.js bug (?)
		if (this._material)
		{
			this._material.needsUpdate = true;
		}
	}

	/**
	 * Not used anymore as it's no longer placed manually by the user.
	 */
	public checkPlacing(raycaster:THREE.Raycaster, scene:Scene, nodePlacer:NodePlacer):boolean
	{
		var intersects = raycaster.intersectObjects(nodePlacer.hitScene.children);

		var intersect = intersects[0];

		this.hasValidPlace = false;

		if (intersect)
		{

			var intersectedRoof = <THREE.Mesh>intersect.object;
			var intersectedRoofName = intersectedRoof.name;

			if (intersectedRoofName !== Building.ROOF_LEFT &&
				intersectedRoofName !== Building.ROOF_RIGHT)
			{
				// hit object is not roof (may be wall)
				return;
			}

			this.hasValidPlace = true;
			this._mesh.visible = true;

			var point = intersect.point;

			this._pos = point.z;

			this.updatePosition();
		}

		return !!intersect;
	}

	public updatePosition(): boolean
	{
		var config = Main.getInstance().config;
		var metrics = config.metrics;

		//var building_length_half = config.building.length.value / 2;
		//var length_half = this._length / 2;

		var z = this._pos;
		//z = MathUtils.clamp(z, -building_length_half + length_half, building_length_half - length_half);

		this._container.position.z = z;
		this._container.position.x = 0;
		// todo
		this._container.position.y = metrics.top + 0.15;

		return true;
	}

	public setPosition(value:any): boolean
	{
		this._pos = value;

		return this.updatePosition();
	}

	public setLength(value:number)
	{
		// initial length is 10
		this._container.scale.z = value/10;
	}
	public getLength()
	{
		return this._container.scale.z;
	}

	public setZ(value:number)
	{

	}

	public show()
	{
		this._visible = true;

		if (this._mesh)
		{
			this._mesh.visible = true;
		}
	}

	public hide()
	{
		this._visible = false;

		if (this._mesh)
		{
			this._mesh.visible = false;
		}
	}

	public getPosition()
	{
		return this._pos;
	}

	public get container()
	{
		return this._container;
	}

	public get mesh(): THREE.Mesh
	{
		return this._mesh;
	}
}